<template>
  <SmallOutput
    v-if="showSmallOutput && resultsCount === 0"
    :resultsCount
    :options
    @selectedChipId="handleSelectedChipId"
  />
</template>

<script setup lang="ts">
  import { SmallOutput } from '@/entities/filter/small-output';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import type { UiChipsProps } from '@experience/core-chips-ui';
  import { useToursFiltersTitles } from '../../composables/tour/useToursFiltersTitles';
  import type { CityTag, CountryTag, RegionTag } from '@/shared/api/experience';
  import { useTourSmallOutput } from '@/features/smallOutput/tour';
  import type { CurrencyCode } from '@/shared/lib/currency';

  interface Props {
    tags: RegionTag[] | CountryTag[] | CityTag[];
    currencyCode: CurrencyCode;
  }

  const props = defineProps<Props>();

  const filtersStore = useTourFiltersStore();
  const { showSmallOutput } = useTourSmallOutput();

  type ChipsModelValue = UiChipsProps['modelValue'];

  const { filterChips, resetters } = useToursFiltersTitles(props.currencyCode);

  const options = computed(() => {
    const chipOptions = [...filterChips.value];

    const foundRubric = props.tags.find((item) => {
      const currentSlug = (item as RegionTag | CityTag)?.slug || (item as CountryTag)?.tag_slug;

      return currentSlug === filtersStore.searchState.tag__slugs;
    });

    const foundRubricSlug = (foundRubric as RegionTag | CityTag)?.slug || (foundRubric as CountryTag)?.tag_slug || '';

    if (foundRubric && foundRubricSlug !== 'all') {
      chipOptions.push({
        value: 'chip',
        label: foundRubric.name || '',
      });
    }

    return chipOptions;
  });

  const resultsCount = computed(() => filtersStore.searchTotalCount || 0);

  const handleSelectedChipId = (chipId: ChipsModelValue) => {
    const chipKey = chipId as string;

    if (chipKey in resetters) {
      resetters[chipKey as keyof typeof resetters]();
    }

    if (chipKey === 'chip') {
      filtersStore.reset('tag__slugs');
    }

    filtersStore.search();
  };
</script>
